import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Loader2, LogOut, Users, ClipboardList, AlertTriangle, RefreshCw, Download, IndianRupee, Search, Tag, Trash2, Plus, Power, TrendingUp, Pill, Beaker, CalendarClock, CalendarX, Wine, Droplet, MessageSquare, Globe } from "lucide-react";
import AssessmentDetailDialog from "@/components/admin/AssessmentDetailDialog";
import {
  adminLogin,
  adminMe,
  adminStats,
  adminBookings,
  adminAssessments,
  adminPayments,
  adminProgramInterest,
  adminOryven,
  listPromos,
  createPromo,
  updatePromo,
  deletePromo,
  getImpactStats,
  updateImpactStats,
  adminOtpStats,
  updateBookingStatus,
  downloadCSV,
  getToken,
  clearToken,
  formatApiErrorDetail,
  adminAssessmentsExport,
  adminWaResend,
  adminWaTest,
} from "@/lib/adminApi";
import { toast } from "@/components/ui/sonner";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

const BAND_HEX = { green: "#22C55E", yellow: "#EAB308", orange: "#F97316", red: "#EF4444" };
const STATUSES = ["requested", "contacted", "scheduled", "completed", "cancelled"];
const STATUS_HEX = {
  requested: "#5B6660",
  contacted: "#C67B5C",
  scheduled: "#0A2E22",
  completed: "#22C55E",
  cancelled: "#EF4444",
};

function ImpactStatsCard() {
  const [live, setLive] = useState(null);
  const [precision, setPrecision] = useState("");
  const [program, setProgram] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await getImpactStats();
      setLive(d);
      setPrecision(String(d.precision_assessments ?? ""));
      setProgram(String(d.program_enrolments ?? ""));
    } catch (e) {
      toast.error("Could not load impact stats");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (precision !== "") payload.precision_assessments = Number(precision);
      if (program !== "") payload.program_enrolments = Number(program);
      await updateImpactStats(payload);
      toast.success("Impact stats updated");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-8 rounded-3xl border border-sa-beige bg-white p-6" data-testid="admin-impact-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-sa-copper/15 text-sa-copper">
            <TrendingUp className="h-5 w-5" strokeWidth={1.8} />
          </span>
          <div>
            <p className="text-[13px] font-semibold uppercase tracking-[0.2em] text-sa-copper">Impact Counters</p>
            <h3 className="font-display text-xl font-medium text-sa-forest">Landing-page impact numbers</h3>
          </div>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 rounded-full border border-sa-beige px-3 py-1.5 text-sm text-sa-forest hover:border-sa-forest/40"
          data-testid="admin-impact-refresh"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-4">
        <div className="rounded-2xl bg-sa-beige/40 p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-sa-muted">Livers Assessed (auto)</p>
          <p className="mt-1.5 font-display text-2xl text-sa-forest" data-testid="admin-live-assessed">{live?.assessed ?? "—"}</p>
        </div>
        <div className="rounded-2xl bg-sa-beige/40 p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-sa-muted">Healthy (green + yellow)</p>
          <p className="mt-1.5 font-display text-2xl text-sa-forest" data-testid="admin-live-healthy">{live?.healthy ?? "—"}</p>
        </div>
        <div className="rounded-2xl bg-sa-beige/40 p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-sa-muted">At-risk (orange + red)</p>
          <p className="mt-1.5 font-display text-2xl text-sa-forest" data-testid="admin-live-risk">{live?.at_risk ?? "—"}</p>
        </div>
        <div className="rounded-2xl bg-sa-beige/40 p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-sa-muted">Satisfaction</p>
          <p className="mt-1.5 font-display text-2xl text-sa-forest">{live?.satisfaction ?? 100}%</p>
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <label className="rounded-2xl border border-sa-beige p-4">
          <span className="block text-[13px] font-medium text-sa-forest">Precision Liver Evaluations Completed</span>
          <input
            type="number"
            min={0}
            value={precision}
            onChange={(e) => setPrecision(e.target.value)}
            className="mt-2 w-full rounded-xl border border-sa-beige bg-white px-3 py-2 text-lg text-sa-forest outline-none focus:border-sa-copper"
            data-testid="admin-impact-precision"
          />
        </label>
        <label className="rounded-2xl border border-sa-beige p-4">
          <span className="block text-[13px] font-medium text-sa-forest">Individuals Enrolled in Program</span>
          <input
            type="number"
            min={0}
            value={program}
            onChange={(e) => setProgram(e.target.value)}
            className="mt-2 w-full rounded-xl border border-sa-beige bg-white px-3 py-2 text-lg text-sa-forest outline-none focus:border-sa-copper"
            data-testid="admin-impact-program"
          />
        </label>
      </div>

      <button
        onClick={save}
        disabled={saving}
        className="mt-4 inline-flex items-center gap-2 rounded-full bg-sa-copper px-5 py-2.5 text-white transition-colors hover:bg-sa-copper-deep disabled:opacity-60"
        data-testid="admin-impact-save"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        Save impact numbers
      </button>
    </div>
  );
}


function OtpDeliveryMeter() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminOtpStats();
      setData(d);
    } catch (e) {
      toast.error("Could not load OTP stats");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  const day = data?.day || { sent: 0, delivered: 0, failed: 0, rate: null };
  const week = data?.week || { sent: 0, delivered: 0, failed: 0, rate: null };
  const rate = day.rate;
  const rateNum = rate == null ? null : Math.round(rate);
  const hue = rate == null ? "#5B6660" : rate >= 95 ? "#16A34A" : rate >= 80 ? "#C67B5C" : "#DC2626";
  const status = rate == null ? "No sends yet" : rate >= 95 ? "Healthy" : rate >= 80 ? "Degraded" : "At risk";
  const strokeDash = 2 * Math.PI * 42;
  const dashOffset = rate == null ? strokeDash : strokeDash * (1 - Math.max(0, Math.min(100, rate)) / 100);

  return (
    <div className="mt-8 rounded-3xl border border-sa-beige bg-white p-6" data-testid="admin-otp-meter">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-sa-forest/10 text-sa-forest">
            <MessageSquare className="h-5 w-5" strokeWidth={1.8} />
          </span>
          <div>
            <p className="text-[13px] font-semibold uppercase tracking-[0.2em] text-sa-copper">OTP Delivery</p>
            <h3 className="font-display text-xl font-medium text-sa-forest">SMS health — spot template & DLT issues early</h3>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-widest"
            style={{ borderColor: `${hue}55`, color: hue }}
            data-testid="admin-otp-status"
          >
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: hue }} />
            {status}
          </span>
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 rounded-full border border-sa-beige px-3 py-1.5 text-sm text-sa-forest hover:border-sa-forest/40"
            data-testid="admin-otp-refresh"
            disabled={loading}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <div className="mt-5 grid gap-5 md:grid-cols-[auto_1fr]">
        <div className="flex items-center justify-center" data-testid="admin-otp-ring">
          <div className="relative h-32 w-32">
            <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
              <circle cx="50" cy="50" r="42" fill="none" stroke="#EBE8E0" strokeWidth="8" />
              <circle
                cx="50" cy="50" r="42" fill="none"
                stroke={hue} strokeWidth="8" strokeLinecap="round"
                strokeDasharray={strokeDash} strokeDashoffset={dashOffset}
                style={{ transition: "stroke-dashoffset 600ms ease, stroke 300ms" }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-display text-2xl font-medium" style={{ color: hue }}>
                {rateNum == null ? "—" : `${rateNum}%`}
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-sa-muted">24h rate</span>
            </div>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-2xl bg-sa-beige/40 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-sa-muted">Last 24h</p>
            <div className="mt-1.5 flex items-baseline gap-3">
              <span className="font-display text-2xl text-sa-forest" data-testid="admin-otp-day-sent">{day.sent}</span>
              <span className="text-[13px] text-sa-muted">sent</span>
            </div>
            <div className="mt-1 text-[12px]">
              <span className="text-emerald-700" data-testid="admin-otp-day-delivered">✓ {day.delivered} delivered</span>
              {day.failed > 0 && (
                <span className="ml-2 text-red-600" data-testid="admin-otp-day-failed">✕ {day.failed} failed</span>
              )}
            </div>
          </div>

          <div className="rounded-2xl bg-sa-beige/40 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-sa-muted">Last 7 days</p>
            <div className="mt-1.5 flex items-baseline gap-3">
              <span className="font-display text-2xl text-sa-forest" data-testid="admin-otp-week-sent">{week.sent}</span>
              <span className="text-[13px] text-sa-muted">sent</span>
              <span className="ml-auto text-[12px] font-medium text-sa-forest" data-testid="admin-otp-week-rate">
                {week.rate == null ? "—" : `${Math.round(week.rate)}% rate`}
              </span>
            </div>
            <div className="mt-1 text-[12px] text-sa-muted">
              {data?.last_hour_delivered ?? 0} delivered in the last hour · mode: {data?.mode || "—"}
            </div>
          </div>

          {data?.provider_breakdown &&
            Object.entries(data.provider_breakdown).map(([prov, b]) => {
              const total = (b.delivered || 0) + (b.failed || 0);
              const pRate = total ? Math.round((b.delivered / total) * 100) : null;
              return (
                <div key={prov} className="col-span-full flex items-center justify-between rounded-2xl border border-sa-beige px-4 py-2.5 text-[12px]" data-testid={`admin-otp-provider-${prov}`}>
                  <span className="font-semibold uppercase tracking-wider text-sa-forest">{prov}</span>
                  <span className="text-sa-muted">
                    ✓ {b.delivered || 0} · ✕ {b.failed || 0}
                    {pRate != null && <span className="ml-2 font-semibold text-sa-forest">{pRate}%</span>}
                  </span>
                </div>
              );
            })}
        </div>
      </div>

      {data?.recent_failures?.length > 0 && (
        <div className="mt-5 rounded-2xl border border-red-200 bg-red-50/60 p-4" data-testid="admin-otp-failures">
          <div className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-4 w-4" />
            <p className="text-[12px] font-semibold uppercase tracking-widest">Recent failures</p>
          </div>
          <ul className="mt-2 space-y-1.5 text-[13px] text-sa-forest">
            {data.recent_failures.map((f, i) => (
              <li key={i} className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-medium">
                  {f.phone_masked || "phone ?"} <span className="text-sa-muted">· {f.provider || "unknown"}</span>
                </span>
                <span className="max-w-[520px] truncate text-[12px] text-red-700" title={f.error_detail}>
                  {f.error_detail || "unknown error"}
                </span>
                <span className="text-[11px] text-sa-muted">
                  {f.created_at ? new Date(f.created_at).toLocaleString() : ""}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[11px] text-sa-muted">
            Tip: repeated "template" or "DLT" errors → check MSG91 → OTP Templates and confirm the template is Approved with <code>##OTP##</code>.
          </p>
        </div>
      )}
    </div>
  );
}

function WaTester() {
  const [phone, setPhone] = useState("");
  const [band, setBand] = useState("green");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!phone.trim()) {
      toast.error("Enter a test phone number.");
      return;
    }
    setSending(true);
    try {
      const res = await adminWaTest(phone.trim(), band);
      if (res.ok === false) toast.error(res.error || "Send failed");
      else if (res.dry_run) toast.info(`Dry-run OK — template "${res.template || band}" logged, nothing sent (WhatsApp is disabled)`);
      else toast.success(`Sent via template "${res.template || band}"`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-8 rounded-3xl border border-sa-beige bg-white p-6" data-testid="admin-wa-tester">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-sa-forest/10 text-sa-forest">
          <MessageSquare className="h-5 w-5" />
        </span>
        <div>
          <p className="text-[13px] font-semibold uppercase tracking-[0.2em] text-sa-copper">WhatsApp (MSG91)</p>
          <p className="text-sm text-sa-muted">Send a template to a test number. Dry-run while MSG91_WA_ENABLED=false.</p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2.5">
        <input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Test phone e.g. 917778086592"
          data-testid="wa-test-phone"
          className="w-64 rounded-full border border-sa-beige bg-sa-bg px-4 py-2.5 text-sm text-sa-forest outline-none focus:border-sa-copper"
        />
        <Select value={band} onValueChange={setBand}>
          <SelectTrigger className="w-48 rounded-full border-sa-beige bg-sa-bg" data-testid="wa-test-band">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {["green", "yellow", "orange", "red", "reminder", "annual"].map((b) => (
              <SelectItem key={b} value={b} data-testid={`wa-test-band-${b}`}>{b}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button
          onClick={send}
          disabled={sending}
          data-testid="wa-test-send"
          className="inline-flex items-center gap-2 rounded-full bg-sa-forest px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-sa-forest-soft disabled:opacity-60"
        >
          {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageSquare className="h-3.5 w-3.5" />}
          Send test
        </button>
      </div>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null=checking, false=login, true=dashboard

  useEffect(() => {
    if (!getToken()) {
      setAuthed(false);
      return;
    }
    adminMe()
      .then(() => setAuthed(true))
      .catch(() => {
        clearToken();
        setAuthed(false);
      });
  }, []);

  if (authed === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-sa-beige">
        <Loader2 className="h-8 w-8 animate-spin text-sa-copper" />
      </div>
    );
  }

  return authed ? <Dashboard onLogout={() => setAuthed(false)} /> : <Login onSuccess={() => setAuthed(true)} />;
}

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await adminLogin(email, password);
      toast.success("Welcome back.");
      onSuccess();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-sa-forest px-6">
      <div className="grain absolute inset-0" />
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onSubmit={submit}
        className="relative w-full max-w-md rounded-[28px] border border-white/10 bg-white p-8 md:p-10"
        data-testid="admin-login-form"
      >
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-sa-forest font-display text-lg font-bold text-white">
            S
          </span>
          <span className="font-display text-lg font-semibold tracking-tight text-sa-forest">
            SuperAge<span className="text-sa-copper">.</span> Admin
          </span>
        </div>
        <h1 className="mt-6 font-display text-2xl font-medium text-sa-forest">Leads Dashboard</h1>
        <p className="mt-1 text-sm text-sa-muted">Sign in to view bookings and assessments.</p>

        <div className="mt-6 space-y-4">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            data-testid="admin-email"
            className="w-full rounded-xl border border-sa-beige bg-sa-bg px-4 py-3 text-sa-forest outline-none transition-colors focus:border-sa-copper"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            data-testid="admin-password"
            className="w-full rounded-xl border border-sa-beige bg-sa-bg px-4 py-3 text-sa-forest outline-none transition-colors focus:border-sa-copper"
          />
          {error && (
            <p className="text-sm text-red-600" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-sa-copper px-6 py-3.5 font-medium text-white transition-[transform,background-color] duration-300 hover:bg-sa-copper-deep hover:scale-[1.01] disabled:opacity-70"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
          </button>
        </div>
      </motion.form>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [promos, setPromos] = useState([]);
  const [programInterest, setProgramInterest] = useState([]);
  const [oryven, setOryven] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingSearch, setBookingSearch] = useState("");
  const [paidOnly, setPaidOnly] = useState(false);
  const [consultsOnly, setConsultsOnly] = useState(false);
  const [assessmentFilter, setAssessmentFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("bookings");
  const [bandFilter, setBandFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, b, a, p, pr, pi, ov] = await Promise.all([
        adminStats(),
        adminBookings(),
        adminAssessments(),
        adminPayments(),
        listPromos(),
        adminProgramInterest(),
        adminOryven(),
      ]);
      setStats(s);
      setBookings(b);
      setAssessments(a);
      setPayments(p);
      setPromos(pr);
      setProgramInterest(pi);
      setOryven(ov);
    } catch (e) {
      toast.error("Session expired. Please sign in again.");
      clearToken();
      onLogout();
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  const resendWa = async (assessmentId) => {
    try {
      const res = await adminWaResend(assessmentId);
      if (res.whatsapp_status === "failed") toast.error(`Retry failed: ${res.whatsapp_error || "unknown"}`);
      else toast.success(`WhatsApp ${res.whatsapp_status || "sent"}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Retry failed");
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const logout = () => {
    clearToken();
    onLogout();
  };

  const changeStatus = async (id, status) => {
    setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
    try {
      await updateBookingStatus(id, status);
      toast.success("Status updated");
    } catch (e) {
      toast.error("Could not update status");
      load();
    }
  };

  const exportBookings = () =>
    downloadCSV("superage-bookings.csv", bookings, [
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "program", label: "Program" },
      { key: "package", label: "Package" },
      { key: "concerns", label: "Special Concerns" },
      { key: "address", label: "Address" },
      { key: "city", label: "City" },
      { key: "pincode", label: "Pincode" },
      { key: "preferred_date", label: "Preferred Date" },
      { key: "status", label: "Status" },
      { key: "created_at", label: "Requested At" },
    ]);

  const exportAssessments = () =>
    downloadCSV("superage-assessments.csv", filteredAssessments, [
      { key: "score", label: "Score" },
      { key: "label", label: "Risk" },
      { key: "band", label: "Band" },
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "glp1_flagged", label: "GLP-1 Candidate" },
      { key: "saroglitazar_flagged", label: "Saroglitazar Candidate" },
      { key: "alcohol_flagged", label: "Regular Drinker" },
      { key: "masld_flagged", label: "MASLD" },
      { key: "red_flag_flagged", label: "Medical Red Flag" },
      { key: "created_at", label: "Created At" },
    ]);

  const exportResearchJSON = async () => {
    try {
      const data = await adminAssessmentsExport();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `superage-research-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.count} assessments`);
    } catch {
      toast.error("Export failed");
    }
  };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
  const inr = (paise) => `₹${((paise || 0) / 100).toLocaleString("en-IN")}`;

  const STAT_CARDS = stats
    ? [
        { icon: ClipboardList, label: "Bookings", value: stats.bookings },
        { icon: Users, label: "Assessments", value: stats.assessments },
        { icon: AlertTriangle, label: "High-risk leads", value: stats.high_risk },
        { icon: Pill, label: "GLP-1 candidates", value: stats.glp1_flagged ?? 0 },
        { icon: Beaker, label: "Saroglitazar candidates", value: stats.saroglitazar_flagged ?? 0 },
        { icon: Wine, label: "Regular drinkers", value: stats.alcohol_flagged ?? 0, onClick: () => { setActiveTab("assessments"); setAssessmentFilter("alcohol"); } },
        { icon: Droplet, label: "MASLD (fatty liver)", value: stats.masld_flagged ?? 0, onClick: () => { setActiveTab("assessments"); setAssessmentFilter("masld"); } },
        { icon: AlertTriangle, label: "Medical red flags", value: stats.red_flag_flagged ?? 0, accent: (stats.red_flag_flagged ?? 0) > 0 ? "danger" : null, onClick: () => { setActiveTab("assessments"); setAssessmentFilter("red_flag"); } },
        { icon: CalendarClock, label: "Follow-ups due today", value: stats.followup_due_today ?? 0, accent: (stats.followup_due_today ?? 0) > 0 ? "warn" : null, onClick: () => { setActiveTab("assessments"); setAssessmentFilter("due_today"); } },
        { icon: CalendarX, label: "Overdue follow-ups", value: stats.followup_overdue ?? 0, accent: (stats.followup_overdue ?? 0) > 0 ? "danger" : null, onClick: () => { setActiveTab("assessments"); setAssessmentFilter("overdue"); } },
        { icon: IndianRupee, label: "Revenue", value: inr(stats.revenue) },
        { icon: Globe, label: "Website visits", value: stats.visits_total ?? 0 },
        { icon: TrendingUp, label: "Visits today", value: stats.visits_today ?? 0 },
      ]
    : [];

  // Filtered bookings (search + paid-only + consultations-only)
  const q = bookingSearch.trim().toLowerCase();
  const filteredBookings = bookings.filter((b) => {
    if (paidOnly && !b.paid) return false;
    if (consultsOnly && b.program !== "online-consultation") return false;
    if (!q) return true;
    return [b.name, b.email, b.phone].filter(Boolean).some((v) => v.toLowerCase().includes(q));
  });
  const consultCount = bookings.filter((b) => b.program === "online-consultation").length;

  // Filtered assessments — for follow-up on precision-therapeutic candidates
  // and for the follow-up scheduler tiles on the dashboard.
  const todayISO = new Date().toISOString().slice(0, 10);
  const CLOSED_OUTCOMES = new Set(["closed", "declined", "booked"]);
  const isOpen = (a) => !CLOSED_OUTCOMES.has(a.followup_outcome || "");
  const filteredAssessments = assessments.filter((a) => {
    if (assessmentFilter === "glp1" && !a.glp1_flagged) return false;
    if (assessmentFilter === "saro" && !a.saroglitazar_flagged) return false;
    if (assessmentFilter === "alcohol" && !a.alcohol_flagged) return false;
    if (assessmentFilter === "masld" && !a.masld_flagged) return false;
    if (assessmentFilter === "red_flag" && !a.red_flag_flagged) return false;
    if (assessmentFilter === "due_today") {
      if (a.followup_next_date !== todayISO || !isOpen(a)) return false;
    }
    if (assessmentFilter === "overdue") {
      if (!a.followup_next_date || a.followup_next_date >= todayISO || !isOpen(a)) return false;
    }
    if (bandFilter && a.band !== bandFilter) return false;
    if (dateFrom) {
      if (!a.created_at || new Date(a.created_at) < new Date(dateFrom)) return false;
    }
    if (dateTo) {
      // include the whole "to" day
      const endOfDay = new Date(dateTo);
      endOfDay.setHours(23, 59, 59, 999);
      if (!a.created_at || new Date(a.created_at) > endOfDay) return false;
    }
    return true;
  });

  // Leads trend — last 8 weeks (bookings + assessments by created_at)
  const chartData = (() => {
    const weeks = [];
    const now = new Date();
    for (let i = 7; i >= 0; i--) {
      const start = new Date(now);
      start.setDate(now.getDate() - i * 7);
      start.setHours(0, 0, 0, 0);
      weeks.push({ start, label: `${start.getDate()}/${start.getMonth() + 1}`, assessments: 0, bookings: 0 });
    }
    const bucket = (iso, key) => {
      if (!iso) return;
      const d = new Date(iso).getTime();
      for (let i = weeks.length - 1; i >= 0; i--) {
        if (d >= weeks[i].start.getTime()) {
          weeks[i][key] += 1;
          break;
        }
      }
    };
    assessments.forEach((a) => bucket(a.created_at, "assessments"));
    bookings.forEach((b) => bucket(b.created_at, "bookings"));
    return weeks.map(({ label, assessments, bookings }) => ({ label, assessments, bookings }));
  })();

  const reloadPromos = async () => {
    try {
      setPromos(await listPromos());
    } catch (e) {
      toast.error("Could not refresh promos");
    }
  };

  return (
    <div className="min-h-screen bg-sa-bg" data-testid="admin-dashboard">
      <header className="sticky top-0 z-10 border-b border-sa-beige bg-sa-bg/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-sa-forest font-display text-lg font-bold text-white">
              S
            </span>
            <span className="font-display text-lg font-semibold tracking-tight text-sa-forest">
              Leads Dashboard
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              className="inline-flex items-center gap-2 rounded-full border border-sa-beige px-4 py-2 text-sm font-medium text-sa-forest transition-colors hover:border-sa-forest/40"
              data-testid="admin-refresh"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full bg-sa-forest px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-sa-forest-soft"
              data-testid="admin-logout"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 py-10">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {STAT_CARDS.map((c) => {
            const Icon = c.icon;
            const accentBg =
              c.accent === "danger" ? "bg-red-50 border-red-200" :
              c.accent === "warn" ? "bg-amber-50 border-amber-200" :
              "bg-white border-sa-beige";
            const accentIcon =
              c.accent === "danger" ? "text-red-600" :
              c.accent === "warn" ? "text-amber-600" :
              "text-sa-copper";
            const accentNum =
              c.accent === "danger" ? "text-red-700" :
              c.accent === "warn" ? "text-amber-700" :
              "text-sa-forest";
            return (
              <div
                key={c.label}
                onClick={c.onClick}
                className={`rounded-[24px] border p-6 transition-all ${accentBg} ${c.onClick ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-md" : ""}`}
              >
                <Icon className={`h-5 w-5 ${accentIcon}`} />
                <p className={`mt-4 font-display text-4xl font-medium ${accentNum}`}>{c.value}</p>
                <p className="text-sm text-sa-muted">{c.label}</p>
              </div>
            );
          })}
        </div>

        {stats?.visits_daily?.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2 text-[13px] text-sa-muted" data-testid="admin-visits-daily">
            <span className="font-medium text-sa-forest">Daily visits (IST):</span>
            {stats.visits_daily.map((d) => (
              <span
                key={d.day}
                className="rounded-full border border-sa-beige bg-white px-3 py-1"
                data-testid={`visit-day-${d.day}`}
              >
                {new Date(`${d.day}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} · <b className="text-sa-forest">{d.count}</b>
              </span>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-sa-copper" />
          </div>
        ) : (
          <>
          <div className="mt-6 rounded-[24px] border border-sa-beige bg-white p-6" data-testid="leads-chart">
            <div className="mb-5 flex items-center justify-between">
              <p className="font-display text-lg font-medium text-sa-forest">Leads — last 8 weeks</p>
              <div className="flex items-center gap-4 text-xs text-sa-muted">
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-sa-forest" /> Assessments</span>
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-sa-copper" /> Bookings</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EBE8E0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#5B6660" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#5B6660" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "rgba(10,46,34,0.04)" }} contentStyle={{ borderRadius: 12, border: "1px solid #EBE8E0" }} />
                <Bar dataKey="assessments" fill="#0A2E22" radius={[6, 6, 0, 0]} maxBarSize={26} />
                <Bar dataKey="bookings" fill="#C67B5C" radius={[6, 6, 0, 0]} maxBarSize={26} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <ImpactStatsCard />

          <OtpDeliveryMeter />
          <WaTester />

          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-8">
            <TabsList className="bg-sa-beige">
              <TabsTrigger value="bookings" data-testid="tab-bookings">
                Bookings ({bookings.length})
              </TabsTrigger>
              <TabsTrigger value="assessments" data-testid="tab-assessments">
                Assessments ({assessments.length})
              </TabsTrigger>
              <TabsTrigger value="payments" data-testid="tab-payments">
                Payments ({payments.length})
              </TabsTrigger>
              <TabsTrigger value="promos" data-testid="tab-promos">
                Promo Codes ({promos.length})
              </TabsTrigger>
              <TabsTrigger value="program-interest" data-testid="tab-program-interest">
                Reset My Liver ({programInterest.length})
              </TabsTrigger>
              <TabsTrigger value="oryven" data-testid="tab-oryven">
                ORYVEN ({oryven?.total ?? 0})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="bookings" className="mt-6">
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-sa-muted" />
                    <input
                      value={bookingSearch}
                      onChange={(e) => setBookingSearch(e.target.value)}
                      placeholder="Search name, email, phone"
                      data-testid="booking-search"
                      className="w-64 rounded-full border border-sa-beige bg-white py-2 pl-9 pr-4 text-sm text-sa-forest outline-none focus:border-sa-copper"
                    />
                  </div>
                  <button
                    onClick={() => setPaidOnly((v) => !v)}
                    data-testid="paid-filter-toggle"
                    className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                      paidOnly
                        ? "border-risk-green bg-risk-green/10 text-sa-forest"
                        : "border-sa-beige bg-white text-sa-muted hover:border-sa-forest/40"
                    }`}
                  >
                    <span className={`h-2 w-2 rounded-full ${paidOnly ? "bg-risk-green" : "bg-sa-muted/40"}`} />
                    Paid only
                  </button>
                  <button
                    onClick={() => setConsultsOnly((v) => !v)}
                    data-testid="consult-filter-toggle"
                    className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                      consultsOnly
                        ? "border-sa-copper bg-sa-copper/10 text-sa-forest"
                        : "border-sa-beige bg-white text-sa-muted hover:border-sa-copper/40"
                    }`}
                  >
                    <span className={`h-2 w-2 rounded-full ${consultsOnly ? "bg-sa-copper" : "bg-sa-muted/40"}`} />
                    Consultations ({consultCount})
                  </button>
                </div>
                <button
                  onClick={exportBookings}
                  data-testid="export-bookings"
                  className="inline-flex items-center gap-2 rounded-full border border-sa-beige bg-white px-4 py-2 text-sm font-medium text-sa-forest transition-colors hover:border-sa-forest/40"
                >
                  <Download className="h-4 w-4" /> Export CSV
                </button>
              </div>
              <div className="overflow-hidden rounded-[20px] border border-sa-beige bg-white">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-sa-beige/50 text-[12px] uppercase tracking-wider text-sa-muted">
                      <tr>
                        <th className="px-5 py-3">Name</th>
                        <th className="px-5 py-3">Contact</th>
                        <th className="px-5 py-3">Package</th>
                        <th className="px-5 py-3">Collection Address</th>
                        <th className="px-5 py-3">Preferred</th>
                        <th className="px-5 py-3">Status</th>
                        <th className="px-5 py-3">Requested</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-sa-beige">
                      {filteredBookings.length === 0 && (
                        <tr><td className="px-5 py-6 text-sa-muted" colSpan={7}>No bookings match your filters.</td></tr>
                      )}
                      {filteredBookings.map((b) => (
                        <tr
                          key={b.id}
                          className={`text-sa-forest ${b.program === "online-consultation" ? "border-l-4 border-l-sa-copper bg-sa-copper/[0.06]" : ""}`}
                          data-testid={`booking-row-${b.id}`}
                        >
                          <td className="px-5 py-3 font-medium">
                            <div className="flex items-center gap-2">
                              {b.name}
                              {b.paid && (
                                <span
                                  className="rounded-full bg-risk-green px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white"
                                  data-testid={`paid-badge-${b.id}`}
                                >
                                  Paid
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-5 py-3 text-sa-muted">{b.email}<br />{b.phone}</td>
                          <td className="px-5 py-3" data-testid={`package-cell-${b.id}`}>
                            {b.program === "priority-consultation" ? (
                              <span
                                className="inline-flex rounded-full bg-risk-red/15 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-risk-red"
                                data-testid={`priority-badge-${b.id}`}
                              >
                                Priority
                              </span>
                            ) : b.program === "online-consultation" ? (
                              <span
                                className="inline-flex rounded-full bg-sa-copper/15 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-sa-copper-deep"
                                data-testid={`consult-badge-${b.id}`}
                              >
                                Consult
                              </span>
                            ) : b.package === "advanced" ? (
                              <span
                                className="inline-flex rounded-full bg-risk-orange/15 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-risk-orange"
                                data-testid={`package-badge-${b.id}`}
                              >
                                Advance
                              </span>
                            ) : (
                              <span className="inline-flex rounded-full bg-sa-forest/10 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-sa-forest">
                                Basic
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-3 text-sa-muted" data-testid={`booking-address-cell-${b.id}`}>
                            {b.address ? (
                              <>{b.address}<br />{b.city}{b.pincode ? ` — ${b.pincode}` : ""}</>
                            ) : b.concerns ? (
                              <span className="italic">Concerns: {b.concerns}</span>
                            ) : "—"}
                          </td>
                          <td className="px-5 py-3">{b.preferred_date || "—"}</td>
                          <td className="px-5 py-3">
                            <Select value={b.status} onValueChange={(v) => changeStatus(b.id, v)}>
                              <SelectTrigger
                                data-testid={`status-select-${b.id}`}
                                className="h-9 w-[140px] rounded-full border-0 text-white"
                                style={{ backgroundColor: STATUS_HEX[b.status] || "#5B6660" }}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {STATUSES.map((s) => (
                                  <SelectItem key={s} value={s} className="capitalize">
                                    {s}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-5 py-3 text-sa-muted">{fmtDate(b.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="assessments" className="mt-6">
              <div className="mb-4 space-y-3 rounded-2xl border border-sa-beige bg-white p-4">
                {/* Row 1 — flag filter + export */}
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    {[
                      { key: "all",  label: "All" },
                      { key: "glp1", label: "GLP-1 candidates", Icon: Pill },
                      { key: "saro", label: "Saroglitazar candidates", Icon: Beaker },
                      { key: "alcohol", label: "Regular drinkers", Icon: Wine },
                      { key: "masld", label: "MASLD", Icon: Droplet },
                      { key: "red_flag", label: "Medical red flag", Icon: AlertTriangle },
                      { key: "due_today", label: "Due today", Icon: CalendarClock },
                      { key: "overdue",   label: "Overdue",   Icon: CalendarX },
                    ].map((f) => (
                      <button
                        key={f.key}
                        onClick={() => setAssessmentFilter(f.key)}
                        data-testid={`filter-${f.key}`}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[13px] font-medium transition-colors ${
                          assessmentFilter === f.key
                            ? "border-sa-forest bg-sa-forest text-white"
                            : "border-sa-beige bg-white text-sa-forest hover:border-sa-forest/40"
                        }`}
                      >
                        {f.Icon && <f.Icon className="h-3.5 w-3.5" />}
                        {f.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={exportAssessments}
                      data-testid="export-assessments"
                      className="inline-flex items-center gap-2 rounded-full border border-sa-beige bg-white px-4 py-2 text-sm font-medium text-sa-forest transition-colors hover:border-sa-forest/40"
                    >
                      <Download className="h-4 w-4" /> Export CSV
                    </button>
                    <button
                      onClick={exportResearchJSON}
                      data-testid="export-research"
                      className="inline-flex items-center gap-2 rounded-full bg-sa-forest px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-sa-forest/90"
                      title="Downloads every field for every assessment — responses, Q&A, recovery plan, follow-up notes"
                    >
                      <Download className="h-4 w-4" /> Research Export (JSON)
                    </button>
                  </div>
                </div>
                {/* Row 2 — band + date range filters */}
                <div className="flex flex-wrap items-center gap-3 border-t border-sa-beige pt-3">
                  <span className="text-[12px] font-semibold uppercase tracking-wider text-sa-muted">Risk band:</span>
                  {[
                    { key: "",       label: "Any",    hex: null },
                    { key: "green",  label: "Green",  hex: "#22C55E" },
                    { key: "yellow", label: "Yellow", hex: "#EAB308" },
                    { key: "orange", label: "Orange", hex: "#F97316" },
                    { key: "red",    label: "Red",    hex: "#EF4444" },
                  ].map((b) => (
                    <button
                      key={b.key || "any"}
                      onClick={() => setBandFilter(b.key)}
                      data-testid={`band-${b.key || "any"}`}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-medium transition-colors ${
                        bandFilter === b.key
                          ? "border-sa-forest bg-sa-forest text-white"
                          : "border-sa-beige bg-white text-sa-forest hover:border-sa-forest/40"
                      }`}
                    >
                      {b.hex && <span className="h-2 w-2 rounded-full" style={{ backgroundColor: b.hex }} />}
                      {b.label}
                    </button>
                  ))}
                  <span className="ml-4 text-[12px] font-semibold uppercase tracking-wider text-sa-muted">From:</span>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    data-testid="date-from"
                    className="rounded-lg border border-sa-beige bg-white px-3 py-1.5 text-[13px] text-sa-forest focus:border-sa-forest focus:outline-none"
                  />
                  <span className="text-[12px] font-semibold uppercase tracking-wider text-sa-muted">To:</span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    data-testid="date-to"
                    className="rounded-lg border border-sa-beige bg-white px-3 py-1.5 text-[13px] text-sa-forest focus:border-sa-forest focus:outline-none"
                  />
                  {(bandFilter || dateFrom || dateTo) && (
                    <button
                      onClick={() => { setBandFilter(""); setDateFrom(""); setDateTo(""); }}
                      className="text-[12px] font-medium text-sa-copper underline"
                      data-testid="clear-filters"
                    >
                      Clear
                    </button>
                  )}
                  <span className="ml-auto text-[12px] font-medium text-sa-muted">
                    {filteredAssessments.length} of {assessments.length}
                  </span>
                </div>
              </div>
              <div className="overflow-hidden rounded-[20px] border border-sa-beige bg-white">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-sa-beige/50 text-[12px] uppercase tracking-wider text-sa-muted">
                      <tr>
                        <th className="px-5 py-3">Score</th>
                        <th className="px-5 py-3">Risk</th>
                        <th className="px-5 py-3">Name</th>
                        <th className="px-5 py-3">Contact</th>
                        <th className="px-5 py-3">Flags</th>
                        <th className="px-5 py-3">When</th>
                        <th className="px-5 py-3">WhatsApp</th>
                        <th className="px-5 py-3">Appointment</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-sa-beige">
                      {filteredAssessments.length === 0 && (
                        <tr><td className="px-5 py-6 text-sa-muted" colSpan={8}>No assessments match this filter.</td></tr>
                      )}
                      {filteredAssessments.map((a) => (
                        <tr
                          key={a.id}
                          onClick={() => setDetailId(a.id)}
                          className="cursor-pointer text-sa-forest transition-colors hover:bg-sa-beige/30"
                          data-testid={`assessment-row-${a.id}`}
                        >
                          <td className="px-5 py-3 font-display text-lg font-medium">{a.score}</td>
                          <td className="px-5 py-3">
                            <span
                              className="inline-flex rounded-full px-2.5 py-0.5 text-[12px] font-semibold text-white"
                              style={{ backgroundColor: BAND_HEX[a.band] || "#5B6660" }}
                            >
                              {a.label}
                            </span>
                          </td>
                          <td className="px-5 py-3">{a.name || "—"}</td>
                          <td className="px-5 py-3 text-sa-muted">{a.email || "—"}<br />{a.phone || ""}</td>
                          <td className="px-5 py-3">
                            <div className="flex flex-wrap gap-1.5">
                              {a.glp1_flagged && (
                                <span
                                  data-testid="badge-glp1"
                                  className="inline-flex items-center gap-1 rounded-full bg-sa-copper/15 px-2 py-0.5 text-[11px] font-semibold text-sa-copper"
                                  title="Meets GLP-1 receptor agonist criteria"
                                >
                                  <Pill className="h-3 w-3" /> GLP-1
                                </span>
                              )}
                              {a.saroglitazar_flagged && (
                                <span
                                  data-testid="badge-saro"
                                  className="inline-flex items-center gap-1 rounded-full bg-sa-forest/10 px-2 py-0.5 text-[11px] font-semibold text-sa-forest"
                                  title="Saroglitazar candidate"
                                >
                                  <Beaker className="h-3 w-3" /> Saro
                                </span>
                              )}
                              {a.alcohol_flagged && (
                                <span
                                  data-testid="badge-alcohol"
                                  className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-600"
                                  title="Drinks more than once/week"
                                >
                                  <Wine className="h-3 w-3" /> Drinker
                                </span>
                              )}
                              {a.masld_flagged && (
                                <span
                                  data-testid="badge-masld"
                                  className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700"
                                  title="Self-reported MASLD / fatty liver"
                                >
                                  <Droplet className="h-3 w-3" /> MASLD
                                </span>
                              )}
                              {a.red_flag_flagged && (
                                <div className="flex flex-col gap-1">
                                  <span
                                    data-testid="badge-red-flag"
                                    className="inline-flex w-fit items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700"
                                    title={`Medical red flag reported (${a.red_flag_level || "clinical"}) — priority follow-up`}
                                  >
                                    <AlertTriangle className="h-3 w-3" /> Red Flag
                                  </span>
                                  {/* Exactly which red-flag answer(s) the person selected */}
                                  {(a.red_flag_labels || []).map((l) => (
                                    <span
                                      key={l}
                                      className="text-[11px] font-medium leading-snug text-red-700"
                                      data-testid={`red-flag-label-${a.id}`}
                                    >
                                      • {l}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {!a.glp1_flagged && !a.saroglitazar_flagged && !a.alcohol_flagged && !a.masld_flagged && !a.red_flag_flagged && (
                                <span className="text-[12px] text-sa-muted">—</span>
                              )}
                            </div>
                          </td>
                          <td className="px-5 py-3 text-sa-muted">{fmtDate(a.created_at)}</td>
                          <td className="px-5 py-3" data-testid={`wa-cell-${a.id}`}>
                            {a.whatsapp_status ? (
                              <div className="flex flex-col gap-1">
                                <span
                                  className={`inline-flex w-fit rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                                    a.whatsapp_status === "failed"
                                      ? "bg-red-100 text-red-700"
                                      : a.whatsapp_status === "dry-run"
                                        ? "bg-amber-100 text-amber-700"
                                        : "bg-emerald-100 text-emerald-700"
                                  }`}
                                  title={a.whatsapp_error || a.whatsapp_template || ""}
                                >
                                  {a.whatsapp_status}
                                </span>
                                {a.whatsapp_sent_at && (
                                  <span className="text-[11px] text-sa-muted">{fmtDate(a.whatsapp_sent_at)}</span>
                                )}
                                {a.whatsapp_status === "failed" && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); resendWa(a.id); }}
                                    className="inline-flex w-fit items-center gap-1 text-[11px] font-semibold text-sa-copper hover:underline"
                                    data-testid={`wa-resend-${a.id}`}
                                  >
                                    <RefreshCw className="h-3 w-3" /> Retry
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span className="text-[12px] text-sa-muted">—</span>
                            )}
                          </td>
                          <td className="px-5 py-3" data-testid={`appt-cell-${a.id}`}>
                            {a.appointment_status === "booked" ? (
                              <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700">Booked</span>
                            ) : (
                              <span className="inline-flex rounded-full bg-sa-beige/60 px-2.5 py-0.5 text-[11px] font-medium text-sa-muted">Not booked</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="payments" className="mt-6">
              <div className="overflow-hidden rounded-[20px] border border-sa-beige bg-white">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-sa-beige/50 text-[12px] uppercase tracking-wider text-sa-muted">
                      <tr>
                        <th className="px-5 py-3">Amount</th>
                        <th className="px-5 py-3">Status</th>
                        <th className="px-5 py-3">Promo</th>
                        <th className="px-5 py-3">Contact</th>
                        <th className="px-5 py-3">Order ID</th>
                        <th className="px-5 py-3">When</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-sa-beige">
                      {payments.length === 0 && (
                        <tr><td className="px-5 py-6 text-sa-muted" colSpan={6}>No payments yet. Payments will appear here once Razorpay is activated.</td></tr>
                      )}
                      {payments.map((p) => (
                        <tr key={p.id} className="text-sa-forest">
                          <td className="px-5 py-3 font-display text-base font-medium">{inr(p.amount)}</td>
                          <td className="px-5 py-3">
                            <span
                              className="inline-flex rounded-full px-2.5 py-0.5 text-[12px] font-semibold text-white"
                              style={{ backgroundColor: p.status === "paid" ? "#22C55E" : p.status === "failed" ? "#EF4444" : "#5B6660" }}
                            >
                              {p.status}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-sa-muted">{p.promo_code || "—"}</td>
                          <td className="px-5 py-3 text-sa-muted">{p.email || "—"}<br />{p.phone || ""}</td>
                          <td className="px-5 py-3 text-sa-muted">{p.order_id}</td>
                          <td className="px-5 py-3 text-sa-muted">{fmtDate(p.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="promos" className="mt-6">
              <PromoManager promos={promos} onChange={reloadPromos} inr={inr} />
            </TabsContent>

            <TabsContent value="program-interest" className="mt-6">
              <ProgramInterestList rows={programInterest} />
            </TabsContent>

            <TabsContent value="oryven" className="mt-6">
              <OryvenPanel data={oryven} />
            </TabsContent>
          </Tabs>
          </>
        )}
      </div>
      <AssessmentDetailDialog
        id={detailId}
        open={!!detailId}
        onClose={() => setDetailId(null)}
      />
    </div>
  );
}

function ProgramInterestList({ rows }) {
  const [q, setQ] = useState("");
  const norm = (s) => String(s || "").toLowerCase();
  const filtered = rows.filter((r) => {
    if (!q.trim()) return true;
    const needle = norm(q);
    return norm(r.name).includes(needle) || norm(r.phone).includes(needle);
  });

  const exportCsv = () => {
    const columns = [
      { key: "name", label: "Name" },
      { key: "phone", label: "Phone" },
      { key: "created_at", label: "Signed Up" },
    ];
    downloadCSV(`reset-my-liver-${new Date().toISOString().slice(0, 10)}.csv`, filtered, columns);
  };

  return (
    <div data-testid="program-interest-list">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-sa-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name or phone"
            data-testid="program-interest-search"
            className="w-72 rounded-full border border-sa-beige bg-white py-2 pl-9 pr-4 text-sm text-sa-forest outline-none focus:border-sa-copper"
          />
        </div>
        <button
          onClick={exportCsv}
          disabled={filtered.length === 0}
          data-testid="program-interest-export"
          className="inline-flex items-center gap-1.5 rounded-full border border-sa-beige bg-white px-4 py-2 text-sm text-sa-forest hover:border-sa-forest/40 disabled:opacity-40"
        >
          <Download className="h-3.5 w-3.5" /> Export CSV
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-sa-beige bg-sa-beige/30 p-10 text-center text-sm text-sa-muted">
          {rows.length === 0
            ? "No one has tapped Reset My Liver yet. Share the landing page to start collecting interest."
            : "No matches for your search."}
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-sa-beige bg-white">
          <table className="w-full text-sm">
            <thead className="bg-sa-beige/60 text-left text-[11px] font-semibold uppercase tracking-widest text-sa-muted">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Signed Up</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-sa-beige/70">
              {filtered.map((r, i) => (
                <tr key={r.phone + i} data-testid={`program-interest-row-${i}`} className="text-sa-forest">
                  <td className="px-4 py-3 font-medium">{r.name || <span className="text-sa-muted">—</span>}</td>
                  <td className="px-4 py-3">
                    <a href={`tel:${r.phone}`} className="text-sa-copper hover:underline">
                      {r.phone}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-sa-muted">
                    {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function PromoManager({ promos, onChange, inr }) {
  const [code, setCode] = useState("");
  const [type, setType] = useState("percent");
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    if (!code.trim() || !value) {
      toast.error("Enter a code and value");
      return;
    }
    setSaving(true);
    try {
      // flat value entered in rupees -> convert to paise
      const numeric = type === "flat" ? Math.round(Number(value) * 100) : Number(value);
      await createPromo({ code: code.trim().toUpperCase(), type, value: numeric });
      toast.success("Promo code created");
      setCode("");
      setValue("");
      onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create code");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (p) => {
    try {
      await updatePromo(p.id, { active: !p.active });
      onChange();
    } catch (e) {
      toast.error("Could not update code");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete promo code "${p.code}"? This cannot be undone.`)) return;
    try {
      await deletePromo(p.id);
      toast.success("Promo code deleted");
      onChange();
    } catch (e) {
      toast.error("Could not delete code");
    }
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <form
        onSubmit={add}
        className="rounded-[20px] border border-sa-beige bg-white p-6"
        data-testid="promo-create-form"
      >
        <p className="font-display text-lg font-medium text-sa-forest">New promo code</p>
        <div className="mt-4 space-y-3">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="CODE e.g. SAVE20"
            data-testid="promo-new-code"
            className="w-full rounded-xl border border-sa-beige bg-sa-bg px-4 py-2.5 text-sa-forest outline-none focus:border-sa-copper"
          />
          <div className="flex gap-3">
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="promo-new-type" className="h-11 rounded-xl border-sa-beige bg-sa-bg">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="percent">% off</SelectItem>
                <SelectItem value="flat">₹ off</SelectItem>
              </SelectContent>
            </Select>
            <input
              value={value}
              onChange={(e) => setValue(e.target.value.replace(/[^0-9.]/g, ""))}
              placeholder={type === "percent" ? "20" : "500"}
              data-testid="promo-new-value"
              className="w-full rounded-xl border border-sa-beige bg-sa-bg px-4 py-2.5 text-sa-forest outline-none focus:border-sa-copper"
            />
          </div>
          <button
            type="submit"
            disabled={saving}
            data-testid="promo-create-submit"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-sa-copper px-5 py-2.5 font-medium text-white transition-colors hover:bg-sa-copper-deep disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Create code
          </button>
        </div>
      </form>

      <div className="lg:col-span-2 space-y-3" data-testid="promo-list">
        {promos.length === 0 && (
          <div className="rounded-[20px] border border-sa-beige bg-white p-6 text-sa-muted">
            No promo codes yet.
          </div>
        )}
        {promos.map((p) => (
          <div
            key={p.id}
            className="flex items-center justify-between rounded-[18px] border border-sa-beige bg-white p-4"
          >
            <div className="flex items-center gap-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-sa-forest/5 text-sa-copper">
                <Tag className="h-5 w-5" />
              </span>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-display text-lg font-medium text-sa-forest">{p.code}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${p.active ? "bg-risk-green/15 text-risk-green" : "bg-sa-beige text-sa-muted"}`}>
                    {p.active ? "Active" : "Inactive"}
                  </span>
                </div>
                <p className="text-sm text-sa-muted">
                  {p.type === "flat" ? `${inr(p.value)} off` : `${p.value}% off`} · {p.label}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => toggle(p)}
                data-testid={`promo-toggle-${p.code}`}
                title={p.active ? "Deactivate" : "Activate"}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-sa-beige text-sa-forest transition-colors hover:border-sa-forest/40"
              >
                <Power className="h-4 w-4" />
              </button>
              <button
                onClick={() => remove(p)}
                data-testid={`promo-delete-${p.code}`}
                title="Delete"
                className="flex h-9 w-9 items-center justify-center rounded-full border border-sa-beige text-red-500 transition-colors hover:border-red-300 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ORYVEN pre-launch demand tracking — registrations, interest mix, calculator
// funnel and page-visit conversion. Admin-only data; never shown publicly.
function OryvenPanel({ data }) {
  if (!data) {
    return <div className="py-10 text-center text-sm text-sa-muted">Loading ORYVEN stats…</div>;
  }
  const fmtD = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
  const conversion = data.page_visits > 0 ? ((data.total / data.page_visits) * 100).toFixed(1) : null;
  const mini = [
    { label: "Registrations", value: data.total, testid: "oryven-total" },
    { label: "Today", value: data.today, testid: "oryven-today" },
    { label: "Calculator starts", value: data.calc_starts, testid: "oryven-calc-starts" },
    { label: "Calculations done", value: data.calc_completes, testid: "oryven-calc-completes" },
    { label: "Page visits", value: data.page_visits, testid: "oryven-page-visits" },
    { label: "Visit → registration", value: conversion ? `${conversion}%` : "—", testid: "oryven-conversion" },
  ];

  const exportCsv = () => {
    downloadCSV(`oryven-interest-${new Date().toISOString().slice(0, 10)}.csv`, data.recent, [
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "interest", label: "Interest" },
      { key: "source", label: "Source" },
      { key: "created_at", label: "Registered" },
    ]);
  };

  return (
    <div data-testid="oryven-panel">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        {mini.map((m) => (
          <div key={m.label} className="rounded-2xl border border-sa-beige bg-white p-4" data-testid={m.testid}>
            <p className="text-[12px] text-sa-muted">{m.label}</p>
            <p className="mt-1 font-display text-2xl font-semibold text-sa-forest">{m.value}</p>
          </div>
        ))}
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-sa-beige bg-white p-5" data-testid="oryven-interest-mix">
          <p className="text-[13px] font-semibold uppercase tracking-[0.14em] text-sa-copper">Interest mix</p>
          {Object.keys(data.by_interest || {}).length === 0 ? (
            <p className="mt-3 text-sm text-sa-muted">No interest selections yet.</p>
          ) : (
            <div className="mt-3 flex flex-wrap gap-2">
              {Object.entries(data.by_interest).map(([k, v]) => (
                <span key={k} className="rounded-full border border-sa-beige bg-sa-bg px-3 py-1.5 text-[13px] text-sa-forest">
                  {k} · <b>{v}</b>
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="rounded-2xl border border-sa-beige bg-white p-5" data-testid="oryven-source-mix">
          <p className="text-[13px] font-semibold uppercase tracking-[0.14em] text-sa-copper">Traffic sources</p>
          {Object.keys(data.by_source || {}).length === 0 ? (
            <p className="mt-3 text-sm text-sa-muted">No source tags yet — use /oryven?src=qr (or any tag) in links.</p>
          ) : (
            <div className="mt-3 flex flex-wrap gap-2">
              {Object.entries(data.by_source).map(([k, v]) => (
                <span key={k} className="rounded-full border border-sa-beige bg-sa-bg px-3 py-1.5 text-[13px] text-sa-forest">
                  {k} · <b>{v}</b>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-5 rounded-2xl border border-sa-beige bg-white" data-testid="oryven-registrations">
        <div className="flex items-center justify-between border-b border-sa-beige px-5 py-3">
          <p className="text-sm font-semibold text-sa-forest">Recent registrations</p>
          <button
            onClick={exportCsv}
            disabled={(data.recent || []).length === 0}
            data-testid="oryven-export"
            className="inline-flex items-center gap-1.5 rounded-full border border-sa-beige bg-white px-4 py-2 text-sm text-sa-forest hover:border-sa-forest/40 disabled:opacity-40"
          >
            <Download className="h-3.5 w-3.5" /> Export CSV
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="text-left text-[12px] uppercase tracking-wide text-sa-muted">
                <th className="px-5 py-2.5 font-medium">Name</th>
                <th className="px-5 py-2.5 font-medium">Email</th>
                <th className="px-5 py-2.5 font-medium">Phone</th>
                <th className="px-5 py-2.5 font-medium">Interest</th>
                <th className="px-5 py-2.5 font-medium">Source</th>
                <th className="px-5 py-2.5 font-medium">Registered</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-sa-beige/70">
              {(data.recent || []).map((r, i) => (
                <tr key={r.id || i} className="text-sa-forest" data-testid={`oryven-row-${i}`}>
                  <td className="px-5 py-3">{r.name}</td>
                  <td className="px-5 py-3">{r.email}</td>
                  <td className="px-5 py-3">{r.phone || "—"}</td>
                  <td className="px-5 py-3">{r.interest || "—"}</td>
                  <td className="px-5 py-3">{r.source || "—"}</td>
                  <td className="px-5 py-3 text-sa-muted">{fmtD(r.created_at)}</td>
                </tr>
              ))}
              {(data.recent || []).length === 0 && (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-sa-muted">No registrations yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
